import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthDialog from '../components/AuthDialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  CheckCircle, ArrowLeft, ArrowRight, Thermometer, Snowflake, Refrigerator, 
  Zap, Droplets, Wrench, Clock, Calendar, MapPin, Camera, X,
  AlertCircle, Shield, Star, Home, Settings, Package, Flame, Wind, WashingMachine
} from 'lucide-react';
import { trackServiceRequest } from '../components/GoogleAdsTracker';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceRequestForm = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    serviceType: '',
    subService: '',
    problemDetail: '',
    brand: '',
    deviceAge: '',
    additionalInfo: '',
    images: [],
    city: '',
    district: '',
    address: '',
    urgency: '',
    scheduledDate: '',
    scheduledTime: ''
  });
  
  // AI Fiyat Tahmini State
  const [aiPriceEstimate, setAiPriceEstimate] = useState(null);
  const [aiPriceLoading, setAiPriceLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Resim yükleme fonksiyonu
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Max 3 resim kontrolü
    if (formData.images.length + files.length > 3) {
      alert('En fazla 3 resim yükleyebilirsiniz');
      return;
    }
    
    setUploadingImage(true);
    const token = localStorage.getItem('auth_token');
    
    for (const file of files) {
      // Dosya boyutu kontrolü (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} dosyası 5MB'dan büyük`);
        continue;
      }
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      try {
        // Token varsa normal endpoint, yoksa public endpoint kullan
        const uploadUrl = token 
          ? `${API_URL}/api/upload/image` 
          : `${API_URL}/api/upload/image-public`;
        
        const headers = { 'Content-Type': 'multipart/form-data' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await axios.post(
          uploadUrl,
          formDataUpload,
          { headers }
        );
        
        if (response.data.success) {
          setFormData(prev => ({
            ...prev,
            images: [...prev.images, response.data.image_url]
          }));
        }
      } catch (error) {
        console.error('Resim yüklenemedi:', error);
        alert('Resim yüklenirken hata oluştu: ' + (typeof error.response?.data?.detail === 'string' ? error.response.data.detail : error.message));
      }
    }
    
    setUploadingImage(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Resim silme fonksiyonu
  const handleRemoveImage = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove)
    }));
  };

  // Kategori bilgileri
  const categoryInfo = {
    kombi: { name: 'Kombi Servisi', icon: Thermometer, color: 'from-orange-500 to-red-500' },
    klima: { name: 'Klima Servisi', icon: Snowflake, color: 'from-blue-500 to-cyan-500' },
    beyaz_esya: { name: 'Beyaz Eşya', icon: Refrigerator, color: 'from-teal-500 to-green-500' },
    elektrik: { name: 'Elektrik Servisi', icon: Zap, color: 'from-yellow-500 to-orange-500' },
    su_tesisati: { name: 'Su Tesisatı', icon: Droplets, color: 'from-cyan-500 to-blue-500' },
    genel: { name: 'Genel Servis', icon: Wrench, color: 'from-gray-500 to-gray-700' }
  };

  // Dinamik form yapısı - Her kategori için özel sorular
  const formStructure = {
    kombi: {
      step1: {
        title: 'Ne tür bir hizmet istiyorsunuz?',
        type: 'single',
        options: [
          { id: 'bakim', label: 'Kombi Bakımı', desc: 'Periyodik bakım, temizlik', icon: Settings },
          { id: 'ariza', label: 'Arıza / Tamir', desc: 'Çalışmıyor, sorun var', icon: Wrench },
          { id: 'montaj', label: 'Montaj', desc: 'Yeni kombi takılacak', icon: Package },
          { id: 'petek', label: 'Petek Temizliği', desc: 'Petek bakım ve temizlik', icon: Flame },
        ]
      },
      step2_bakim: {
        title: 'Kombinin markası nedir?',
        type: 'single',
        options: [
          { id: 'bosch', label: 'Bosch' },
          { id: 'vaillant', label: 'Vaillant' },
          { id: 'baymak', label: 'Baymak' },
          { id: 'demirdokum', label: 'Demirdöküm' },
          { id: 'arcelik', label: 'Arçelik' },
          { id: 'buderus', label: 'Buderus' },
          { id: 'viessmann', label: 'Viessmann' },
          { id: 'diger', label: 'Diğer / Bilmiyorum' },
        ]
      },
      step2_ariza: {
        title: 'Kombide ne sorun var?',
        type: 'single',
        options: [
          { id: 'yanmiyor', label: 'Yanmıyor / Çalışmıyor', desc: 'Kombi hiç çalışmıyor' },
          { id: 'su_isitmiyor', label: 'Su Isıtmıyor', desc: 'Sıcak su gelmiyor' },
          { id: 'kalorifer_isitmiyor', label: 'Kalorifer Isıtmıyor', desc: 'Petekler ısınmıyor' },
          { id: 'ses_yapiyor', label: 'Ses Yapıyor', desc: 'Anormal ses geliyor' },
          { id: 'su_akitiyor', label: 'Su Akıtıyor / Damlıyor', desc: 'Su kaçağı var' },
          { id: 'hata_kodu', label: 'Hata Kodu Veriyor', desc: 'Ekranda hata var' },
          { id: 'diger', label: 'Diğer Sorun', desc: 'Başka bir sorun' },
        ]
      },
      step2_montaj: {
        title: 'Montaj detayları',
        type: 'single',
        options: [
          { id: 'yeni_montaj', label: 'Yeni Kombi Montajı', desc: 'İlk kez takılacak' },
          { id: 'degisim', label: 'Eski Kombi Değişimi', desc: 'Mevcut kombi değişecek' },
          { id: 'yer_degisikligi', label: 'Yer Değişikliği', desc: 'Kombi başka yere taşınacak' },
        ]
      },
      step2_petek: {
        title: 'Kaç petek temizlenecek?',
        type: 'single',
        options: [
          { id: '1-3', label: '1-3 Petek' },
          { id: '4-6', label: '4-6 Petek' },
          { id: '7-10', label: '7-10 Petek' },
          { id: '10+', label: '10\'dan Fazla' },
        ]
      },
      step3: {
        title: 'Kombinin yaşı nedir?',
        type: 'single',
        options: [
          { id: '0-2', label: '0-2 Yıl', desc: 'Yeni' },
          { id: '3-5', label: '3-5 Yıl' },
          { id: '6-10', label: '6-10 Yıl' },
          { id: '10+', label: '10 Yıldan Fazla', desc: 'Eski' },
          { id: 'bilmiyorum', label: 'Bilmiyorum' },
        ]
      }
    },
    klima: {
      step1: {
        title: 'Ne tür bir hizmet istiyorsunuz?',
        type: 'single',
        options: [
          { id: 'bakim', label: 'Klima Bakımı', desc: 'Temizlik ve bakım', icon: Settings },
          { id: 'ariza', label: 'Arıza / Tamir', desc: 'Çalışmıyor, soğutmuyor', icon: Wrench },
          { id: 'montaj', label: 'Montaj', desc: 'Yeni klima takılacak', icon: Package },
          { id: 'gaz', label: 'Gaz Dolumu', desc: 'Freon / Gaz takviyesi', icon: Wind },
          { id: 'sokum', label: 'Söküm', desc: 'Klima sökülecek', icon: Wrench },
        ]
      },
      step2_bakim: {
        title: 'Kaç adet klima bakımı yapılacak?',
        type: 'single',
        options: [
          { id: '1', label: '1 Klima' },
          { id: '2', label: '2 Klima' },
          { id: '3', label: '3 Klima' },
          { id: '4+', label: '4 ve Üzeri' },
        ]
      },
      step2_ariza: {
        title: 'Klimada ne sorun var?',
        type: 'single',
        options: [
          { id: 'sogutmuyor', label: 'Soğutmuyor', desc: 'Soğuk hava gelmiyor' },
          { id: 'isitmiyor', label: 'Isıtmıyor', desc: 'Sıcak hava gelmiyor' },
          { id: 'calismior', label: 'Çalışmıyor', desc: 'Hiç açılmıyor' },
          { id: 'ses', label: 'Ses Yapıyor', desc: 'Anormal ses' },
          { id: 'su_akitiyor', label: 'Su Akıtıyor', desc: 'İç üniteden su damlıyor' },
          { id: 'koku', label: 'Kötü Koku', desc: 'Kötü koku yayıyor' },
          { id: 'diger', label: 'Diğer' },
        ]
      },
      step2_montaj: {
        title: 'Kaç adet klima montajı yapılacak?',
        type: 'single',
        options: [
          { id: '1', label: '1 Klima' },
          { id: '2', label: '2 Klima' },
          { id: '3+', label: '3 ve Üzeri' },
        ]
      },
      step2_gaz: {
        title: 'Klimanın markası nedir?',
        type: 'single',
        options: [
          { id: 'samsung', label: 'Samsung' },
          { id: 'lg', label: 'LG' },
          { id: 'daikin', label: 'Daikin' },
          { id: 'mitsubishi', label: 'Mitsubishi' },
          { id: 'vestel', label: 'Vestel' },
          { id: 'arcelik', label: 'Arçelik' },
          { id: 'diger', label: 'Diğer / Bilmiyorum' },
        ]
      },
      step2_sokum: {
        title: 'Kaç adet klima sökülecek?',
        type: 'single',
        options: [
          { id: '1', label: '1 Klima' },
          { id: '2', label: '2 Klima' },
          { id: '3+', label: '3 ve Üzeri' },
        ]
      }
    },
    beyaz_esya: {
      step1: {
        title: 'Hangi cihaz için servis istiyorsunuz?',
        type: 'single',
        options: [
          { id: 'buzdolabi', label: 'Buzdolabı', icon: Refrigerator },
          { id: 'camasir', label: 'Çamaşır Makinesi', icon: WashingMachine },
          { id: 'bulasik', label: 'Bulaşık Makinesi', icon: WashingMachine },
          { id: 'firin', label: 'Fırın / Ocak', icon: Flame },
          { id: 'kurutma', label: 'Kurutma Makinesi', icon: Wind },
          { id: 'diger', label: 'Diğer Beyaz Eşya', icon: Package },
        ]
      },
      step2: {
        title: 'Ne tür bir hizmet istiyorsunuz?',
        type: 'single',
        options: [
          { id: 'ariza', label: 'Arıza / Tamir', desc: 'Çalışmıyor, sorun var' },
          { id: 'bakim', label: 'Bakım', desc: 'Periyodik bakım' },
          { id: 'montaj', label: 'Montaj / Kurulum', desc: 'Yeni cihaz kurulumu' },
        ]
      },
      step3_ariza: {
        title: 'Cihazda ne sorun var?',
        type: 'single',
        options: [
          { id: 'calismior', label: 'Hiç Çalışmıyor' },
          { id: 'ses', label: 'Ses Yapıyor' },
          { id: 'su_akitiyor', label: 'Su Akıtıyor / Kaçırıyor' },
          { id: 'isitmiyor', label: 'Isıtmıyor / Soğutmuyor' },
          { id: 'titreme', label: 'Titreşim / Sallantı' },
          { id: 'koku', label: 'Kötü Koku' },
          { id: 'diger', label: 'Diğer Sorun' },
        ]
      }
    },
    elektrik: {
      step1: {
        title: 'Ne tür bir elektrik hizmeti istiyorsunuz?',
        type: 'single',
        options: [
          { id: 'ariza', label: 'Arıza / Kesinti', desc: 'Elektrik yok, arıza var', icon: AlertCircle },
          { id: 'priz', label: 'Priz / Anahtar', desc: 'Priz, anahtar montajı', icon: Zap },
          { id: 'aydinlatma', label: 'Aydınlatma', desc: 'Avize, spot montajı', icon: Zap },
          { id: 'tesisat', label: 'Elektrik Tesisatı', desc: 'Kablo çekimi, tesisat', icon: Settings },
          { id: 'sigorta', label: 'Sigorta / Pano', desc: 'Sigorta, pano işleri', icon: Shield },
        ]
      },
      step2_ariza: {
        title: 'Arıza detayı nedir?',
        type: 'single',
        options: [
          { id: 'tamamen_yok', label: 'Elektrik Tamamen Yok' },
          { id: 'kismi', label: 'Bazı Odalarda Yok' },
          { id: 'sigorta_atiyor', label: 'Sigorta Atıyor' },
          { id: 'kivilcim', label: 'Kıvılcım / Yanık Kokusu' },
          { id: 'diger', label: 'Diğer' },
        ]
      },
      step2_priz: {
        title: 'Kaç adet priz/anahtar?',
        type: 'single',
        options: [
          { id: '1-3', label: '1-3 Adet' },
          { id: '4-6', label: '4-6 Adet' },
          { id: '7+', label: '7 ve Üzeri' },
        ]
      },
      step2_aydinlatma: {
        title: 'Ne tür aydınlatma?',
        type: 'single',
        options: [
          { id: 'avize', label: 'Avize Montajı' },
          { id: 'spot', label: 'Spot Montajı' },
          { id: 'aplik', label: 'Aplik Montajı' },
          { id: 'led', label: 'LED Şerit / Panel' },
          { id: 'diger', label: 'Diğer' },
        ]
      }
    },
    su_tesisati: {
      step1: {
        title: 'Ne tür bir tesisat hizmeti istiyorsunuz?',
        type: 'single',
        options: [
          { id: 'tikaniklik', label: 'Tıkanıklık Açma', desc: 'Lavabo, tuvalet tıkalı', icon: Droplets },
          { id: 'kacak', label: 'Su Kaçağı', desc: 'Su sızıntısı, kaçak', icon: AlertCircle },
          { id: 'musluk', label: 'Musluk / Batarya', desc: 'Musluk tamiri, değişimi', icon: Droplets },
          { id: 'tesisat', label: 'Su Tesisatı', desc: 'Boru döşeme, değişim', icon: Settings },
          { id: 'termosifon', label: 'Termosifon / Şofben', desc: 'Tamir, montaj', icon: Flame },
        ]
      },
      step2_tikaniklik: {
        title: 'Nerede tıkanıklık var?',
        type: 'single',
        options: [
          { id: 'lavabo', label: 'Lavabo' },
          { id: 'tuvalet', label: 'Tuvalet / Klozet' },
          { id: 'mutfak', label: 'Mutfak Evyesi' },
          { id: 'banyo', label: 'Banyo Gideri' },
          { id: 'ana_gider', label: 'Ana Gider / Rögar' },
        ]
      },
      step2_kacak: {
        title: 'Su kaçağı nerede?',
        type: 'single',
        options: [
          { id: 'boru', label: 'Boru Patlaması' },
          { id: 'musluk', label: 'Musluk Altı' },
          { id: 'tuvalet', label: 'Tuvalet / Rezervuar' },
          { id: 'duvar', label: 'Duvar İçi / Gizli Kaçak' },
          { id: 'bilmiyorum', label: 'Bilmiyorum / Tespit Gerekli' },
        ]
      },
      step2_musluk: {
        title: 'Ne tür işlem?',
        type: 'single',
        options: [
          { id: 'tamir', label: 'Musluk Tamiri', desc: 'Damlıyor, sızdırıyor' },
          { id: 'degisim', label: 'Musluk Değişimi', desc: 'Yeni musluk takılacak' },
          { id: 'batarya', label: 'Batarya Değişimi', desc: 'Lavabo, evye bataryası' },
        ]
      }
    },
    genel: {
      step1: {
        title: 'Ne tür bir hizmet istiyorsunuz?',
        type: 'single',
        options: [
          { id: 'tamir', label: 'Tamir', icon: Wrench },
          { id: 'bakim', label: 'Bakım', icon: Settings },
          { id: 'montaj', label: 'Montaj', icon: Package },
          { id: 'diger', label: 'Diğer', icon: Home },
        ]
      }
    }
  };

  // Türkiye şehirleri ve ilçeleri - TÜM 81 İL
  const turkeyData = {
    'Adana': ['Seyhan', 'Yüreğir', 'Çukurova', 'Sarıçam', 'Ceyhan', 'Kozan', 'İmamoğlu', 'Karaisalı', 'Karataş', 'Pozantı', 'Aladağ', 'Feke', 'Saimbeyli', 'Tufanbeyli', 'Yumurtalık'],
    'Adıyaman': ['Merkez', 'Kahta', 'Besni', 'Gölbaşı', 'Gerger', 'Samsat', 'Çelikhan', 'Sincik', 'Tut'],
    'Afyonkarahisar': ['Merkez', 'Sandıklı', 'Dinar', 'Bolvadin', 'Emirdağ', 'Çay', 'Sultandağı', 'Sinanpaşa', 'İhsaniye', 'Şuhut'],
    'Ağrı': ['Merkez', 'Patnos', 'Doğubayazıt', 'Diyadin', 'Eleşkirt', 'Tutak', 'Hamur', 'Taşlıçay'],
    'Aksaray': ['Merkez', 'Ortaköy', 'Eskil', 'Gülağaç', 'Güzelyurt', 'Sarıyahşi', 'Ağaçören', 'Sultanhanı'],
    'Amasya': ['Merkez', 'Merzifon', 'Suluova', 'Taşova', 'Gümüşhacıköy', 'Göynücek', 'Hamamözü'],
    'Ankara': ['Çankaya', 'Keçiören', 'Yenimahalle', 'Mamak', 'Etimesgut', 'Sincan', 'Altındağ', 'Pursaklar', 'Gölbaşı', 'Polatlı', 'Beypazarı', 'Çubuk', 'Kahramankazan', 'Akyurt', 'Elmadağ', 'Haymana', 'Şereflikoçhisar', 'Kızılcahamam', 'Nallıhan', 'Ayaş', 'Bala', 'Güdül', 'Kalecik', 'Evren', 'Çamlıdere'],
    'Antalya': ['Muratpaşa', 'Kepez', 'Konyaaltı', 'Aksu', 'Döşemealtı', 'Alanya', 'Manavgat', 'Serik', 'Kumluca', 'Kaş', 'Kemer', 'Finike', 'Gazipaşa', 'Demre', 'Elmalı', 'Korkuteli', 'Akseki', 'Gündoğmuş', 'İbradı'],
    'Ardahan': ['Merkez', 'Göle', 'Çıldır', 'Hanak', 'Posof', 'Damal'],
    'Artvin': ['Merkez', 'Hopa', 'Borçka', 'Arhavi', 'Yusufeli', 'Şavşat', 'Ardanuç', 'Murgul'],
    'Aydın': ['Efeler', 'Nazilli', 'Söke', 'Kuşadası', 'Didim', 'İncirliova', 'Germencik', 'Çine', 'Sultanhisar', 'Köşk', 'Bozdoğan', 'Karacasu', 'Kuyucak', 'Buharkent', 'Yenipazar', 'Koçarlı', 'Karpuzlu'],
    'Balıkesir': ['Altıeylül', 'Karesi', 'Bandırma', 'Edremit', 'Gönen', 'Burhaniye', 'Ayvalık', 'Bigadiç', 'Susurluk', 'Erdek', 'Dursunbey', 'Sındırgı', 'İvrindi', 'Havran', 'Savaştepe', 'Kepsut', 'Manyas', 'Balya', 'Marmara'],
    'Bartın': ['Merkez', 'Amasra', 'Kurucaşile', 'Ulus'],
    'Batman': ['Merkez', 'Kozluk', 'Sason', 'Beşiri', 'Gercüş', 'Hasankeyf'],
    'Bayburt': ['Merkez', 'Demirözü', 'Aydıntepe'],
    'Bilecik': ['Merkez', 'Bozüyük', 'Söğüt', 'Osmaneli', 'Pazaryeri', 'Gölpazarı', 'İnhisar', 'Yenipazar'],
    'Bingöl': ['Merkez', 'Genç', 'Solhan', 'Karlıova', 'Adaklı', 'Kiğı', 'Yayladere', 'Yedisu'],
    'Bitlis': ['Merkez', 'Tatvan', 'Ahlat', 'Güroymak', 'Adilcevaz', 'Hizan', 'Mutki'],
    'Bolu': ['Merkez', 'Gerede', 'Mudurnu', 'Göynük', 'Mengen', 'Seben', 'Yeniçağa', 'Dörtdivan', 'Kıbrıscık'],
    'Burdur': ['Merkez', 'Bucak', 'Gölhisar', 'Yeşilova', 'Tefenni', 'Ağlasun', 'Altınyayla', 'Çavdır', 'Çeltikçi', 'Karamanlı', 'Kemer'],
    'Bursa': ['Osmangazi', 'Yıldırım', 'Nilüfer', 'İnegöl', 'Gemlik', 'Mudanya', 'Mustafakemalpaşa', 'Karacabey', 'Orhangazi', 'Kestel', 'Gürsu', 'İznik', 'Yenişehir', 'Keles', 'Orhaneli', 'Büyükorhan', 'Harmancık'],
    'Çanakkale': ['Merkez', 'Biga', 'Çan', 'Gelibolu', 'Ezine', 'Ayvacık', 'Bayramiç', 'Lapseki', 'Yenice', 'Bozcaada', 'Gökçeada', 'Eceabat'],
    'Çankırı': ['Merkez', 'Çerkeş', 'Ilgaz', 'Kurşunlu', 'Şabanözü', 'Orta', 'Atkaracalar', 'Bayramören', 'Eldivan', 'Kızılırmak', 'Korgun', 'Yapraklı'],
    'Çorum': ['Merkez', 'Sungurlu', 'Osmancık', 'Alaca', 'İskilip', 'Kargı', 'Bayat', 'Mecitözü', 'Ortaköy', 'Oğuzlar', 'Uğurludağ', 'Boğazkale', 'Dodurga', 'Laçin'],
    'Denizli': ['Merkezefendi', 'Pamukkale', 'Çivril', 'Acıpayam', 'Tavas', 'Buldan', 'Sarayköy', 'Kale', 'Çal', 'Honaz', 'Çameli', 'Güney', 'Bekilli', 'Baklan', 'Bozkurt', 'Beyağaç', 'Babadağ', 'Serinhisar'],
    'Diyarbakır': ['Bağlar', 'Kayapınar', 'Yenişehir', 'Sur', 'Bismil', 'Ergani', 'Çınar', 'Silvan', 'Dicle', 'Kulp', 'Lice', 'Hani', 'Eğil', 'Hazro', 'Kocaköy', 'Çermik', 'Çüngüş'],
    'Düzce': ['Merkez', 'Akçakoca', 'Cumayeri', 'Çilimli', 'Gölyaka', 'Gümüşova', 'Kaynaşlı', 'Yığılca'],
    'Edirne': ['Merkez', 'Keşan', 'Uzunköprü', 'Havsa', 'İpsala', 'Enez', 'Meriç', 'Lalapaşa', 'Süloğlu'],
    'Elazığ': ['Merkez', 'Kovancılar', 'Karakoçan', 'Palu', 'Maden', 'Baskil', 'Sivrice', 'Keban', 'Arıcak', 'Alacakaya', 'Ağın'],
    'Erzincan': ['Merkez', 'Tercan', 'Üzümlü', 'Refahiye', 'Çayırlı', 'İliç', 'Kemah', 'Kemaliye', 'Otlukbeli'],
    'Erzurum': ['Yakutiye', 'Palandöken', 'Aziziye', 'Horasan', 'Pasinler', 'Oltu', 'Hınıs', 'Tekman', 'Karayazı', 'Aşkale', 'İspir', 'Tortum', 'Narman', 'Şenkaya', 'Olur', 'Köprüköy', 'Uzundere', 'Çat', 'Karaçoban', 'Pazaryolu'],
    'Eskişehir': ['Odunpazarı', 'Tepebaşı', 'Çifteler', 'Sivrihisar', 'Mahmudiye', 'Seyitgazi', 'Alpu', 'Beylikova', 'İnönü', 'Sarıcakaya', 'Mihalgazi', 'Günyüzü', 'Han', 'Mihalıççık'],
    'Gaziantep': ['Şahinbey', 'Şehitkamil', 'Nizip', 'İslahiye', 'Nurdağı', 'Oğuzeli', 'Araban', 'Yavuzeli', 'Karkamış'],
    'Giresun': ['Merkez', 'Bulancak', 'Espiye', 'Görele', 'Tirebolu', 'Keşap', 'Dereli', 'Yağlıdere', 'Şebinkarahisar', 'Piraziz', 'Eynesil', 'Çanakçı', 'Güce', 'Alucra', 'Çamoluk', 'Doğankent'],
    'Gümüşhane': ['Merkez', 'Kelkit', 'Şiran', 'Torul', 'Köse', 'Kürtün'],
    'Hakkari': ['Merkez', 'Yüksekova', 'Şemdinli', 'Çukurca', 'Derecik'],
    'Hatay': ['Antakya', 'İskenderun', 'Defne', 'Arsuz', 'Payas', 'Dörtyol', 'Kırıkhan', 'Reyhanlı', 'Samandağ', 'Erzin', 'Belen', 'Altınözü', 'Hassa', 'Yayladağı', 'Kumlu'],
    'Iğdır': ['Merkez', 'Tuzluca', 'Aralık', 'Karakoyunlu'],
    'Isparta': ['Merkez', 'Yalvaç', 'Eğirdir', 'Şarkikaraağaç', 'Gelendost', 'Senirkent', 'Keçiborlu', 'Uluborlu', 'Sütçüler', 'Atabey', 'Gönen', 'Aksu', 'Yenişarbademli'],
    'İstanbul': ['Kadıköy', 'Beşiktaş', 'Şişli', 'Üsküdar', 'Bakırköy', 'Ataşehir', 'Maltepe', 'Kartal', 'Pendik', 'Tuzla', 'Beyoğlu', 'Fatih', 'Zeytinburnu', 'Bahçelievler', 'Bağcılar', 'Küçükçekmece', 'Başakşehir', 'Esenyurt', 'Beylikdüzü', 'Sarıyer', 'Beykoz', 'Çekmeköy', 'Sancaktepe', 'Sultanbeyli', 'Ümraniye', 'Eyüpsultan', 'Gaziosmanpaşa', 'Kağıthane', 'Sultangazi', 'Arnavutköy', 'Avcılar', 'Bayrampaşa', 'Esenler', 'Güngören', 'Büyükçekmece', 'Çatalca', 'Silivri', 'Şile', 'Adalar'],
    'İzmir': ['Konak', 'Karşıyaka', 'Bornova', 'Buca', 'Bayraklı', 'Çiğli', 'Gaziemir', 'Karabağlar', 'Narlıdere', 'Balçova', 'Güzelbahçe', 'Aliağa', 'Bergama', 'Çeşme', 'Dikili', 'Foça', 'Kemalpaşa', 'Kiraz', 'Menderes', 'Menemen', 'Ödemiş', 'Seferihisar', 'Selçuk', 'Tire', 'Torbalı', 'Urla', 'Bayındır', 'Beydağ', 'Karaburun', 'Kınık'],
    'Kahramanmaraş': ['Dulkadiroğlu', 'Onikişubat', 'Elbistan', 'Afşin', 'Türkoğlu', 'Pazarcık', 'Göksun', 'Andırın', 'Çağlayancerit', 'Nurhak', 'Ekinözü'],
    'Karabük': ['Merkez', 'Safranbolu', 'Yenice', 'Eflani', 'Eskipazar', 'Ovacık'],
    'Karaman': ['Merkez', 'Ermenek', 'Sarıveliler', 'Ayrancı', 'Kazımkarabekir', 'Başyayla'],
    'Kars': ['Merkez', 'Sarıkamış', 'Kağızman', 'Selim', 'Digor', 'Arpaçay', 'Susuz', 'Akyaka'],
    'Kastamonu': ['Merkez', 'Tosya', 'Taşköprü', 'İnebolu', 'Araç', 'Daday', 'Cide', 'Devrekani', 'Çatalzeytin', 'Abana', 'Küre', 'Azdavay', 'Pınarbaşı', 'Şenpazar', 'Ağlı', 'Doğanyurt', 'Hanönü', 'İhsangazi', 'Seydiler', 'Bozkurt'],
    'Kayseri': ['Melikgazi', 'Kocasinan', 'Talas', 'Hacılar', 'İncesu', 'Develi', 'Yahyalı', 'Bünyan', 'Yeşilhisar', 'Pınarbaşı', 'Sarıoğlan', 'Tomarza', 'Sarız', 'Akkışla', 'Felahiye', 'Özvatan'],
    'Kırıkkale': ['Merkez', 'Yahşihan', 'Keskin', 'Delice', 'Sulakyurt', 'Balışeyh', 'Bahşılı', 'Çelebi', 'Karakeçili'],
    'Kırklareli': ['Merkez', 'Lüleburgaz', 'Babaeski', 'Vize', 'Pınarhisar', 'Demirköy', 'Kofçaz', 'Pehlivanköy'],
    'Kırşehir': ['Merkez', 'Kaman', 'Mucur', 'Çiçekdağı', 'Akpınar', 'Boztepe', 'Akçakent'],
    'Kilis': ['Merkez', 'Musabeyli', 'Elbeyli', 'Polateli'],
    'Kocaeli': ['İzmit', 'Gebze', 'Darıca', 'Körfez', 'Gölcük', 'Derince', 'Kartepe', 'Başiskele', 'Çayırova', 'Dilovası', 'Kandıra', 'Karamürsel'],
    'Konya': ['Selçuklu', 'Meram', 'Karatay', 'Ereğli', 'Akşehir', 'Beyşehir', 'Seydişehir', 'Cihanbeyli', 'Kulu', 'Karapınar', 'Çumra', 'Ilgın', 'Kadınhanı', 'Sarayönü', 'Yunak', 'Bozkır', 'Doğanhisar', 'Hüyük', 'Hadim', 'Taşkent', 'Altınekin', 'Güneysınır', 'Tuzlukçu', 'Akören', 'Çeltik', 'Derbent', 'Derebucak', 'Emirgazi', 'Halkapınar', 'Yalıhüyük', 'Ahırlı'],
    'Kütahya': ['Merkez', 'Tavşanlı', 'Simav', 'Gediz', 'Emet', 'Domaniç', 'Altıntaş', 'Aslanapa', 'Çavdarhisar', 'Dumlupınar', 'Hisarcık', 'Pazarlar', 'Şaphane'],
    'Malatya': ['Battalgazi', 'Yeşilyurt', 'Darende', 'Akçadağ', 'Doğanşehir', 'Hekimhan', 'Pütürge', 'Arguvan', 'Arapgir', 'Yazıhan', 'Kuluncak', 'Kale', 'Doğanyol'],
    'Manisa': ['Şehzadeler', 'Yunusemre', 'Akhisar', 'Turgutlu', 'Salihli', 'Soma', 'Alaşehir', 'Saruhanlı', 'Kırkağaç', 'Demirci', 'Gördes', 'Sarıgöl', 'Selendi', 'Gölmarmara', 'Ahmetli', 'Köprübaşı', 'Kula'],
    'Mardin': ['Artuklu', 'Kızıltepe', 'Midyat', 'Nusaybin', 'Derik', 'Mazıdağı', 'Dargeçit', 'Ömerli', 'Savur', 'Yeşilli'],
    'Mersin': ['Akdeniz', 'Mezitli', 'Toroslar', 'Yenişehir', 'Tarsus', 'Silifke', 'Erdemli', 'Anamur', 'Mut', 'Gülnar', 'Aydıncık', 'Bozyazı', 'Çamlıyayla'],
    'Muğla': ['Menteşe', 'Bodrum', 'Fethiye', 'Marmaris', 'Milas', 'Dalaman', 'Ortaca', 'Köyceğiz', 'Datça', 'Ula', 'Yatağan', 'Kavaklıdere', 'Seydikemer'],
    'Muş': ['Merkez', 'Bulanık', 'Malazgirt', 'Varto', 'Hasköy', 'Korkut'],
    'Nevşehir': ['Merkez', 'Ürgüp', 'Avanos', 'Gülşehir', 'Derinkuyu', 'Kozaklı', 'Acıgöl', 'Hacıbektaş'],
    'Niğde': ['Merkez', 'Bor', 'Çiftlik', 'Ulukışla', 'Altunhisar', 'Çamardı'],
    'Ordu': ['Altınordu', 'Ünye', 'Fatsa', 'Perşembe', 'Korgan', 'Kumru', 'Ulubey', 'Gölköy', 'Mesudiye', 'Akkuş', 'Aybastı', 'Çamaş', 'Çatalpınar', 'Çaybaşı', 'Gülyalı', 'Gürgentepe', 'İkizce', 'Kabadüz', 'Kabataş'],
    'Osmaniye': ['Merkez', 'Kadirli', 'Düziçi', 'Bahçe', 'Toprakkale', 'Hasanbeyli', 'Sumbas'],
    'Rize': ['Merkez', 'Ardeşen', 'Çayeli', 'Pazar', 'Fındıklı', 'Güneysu', 'İkizdere', 'Kalkandere', 'Çamlıhemşin', 'Derepazarı', 'Hemşin', 'İyidere'],
    'Sakarya': ['Adapazarı', 'Serdivan', 'Erenler', 'Arifiye', 'Hendek', 'Akyazı', 'Sapanca', 'Karasu', 'Geyve', 'Pamukova', 'Kaynarca', 'Ferizli', 'Söğütlü', 'Taraklı', 'Kocaali', 'Karapürçek'],
    'Samsun': ['İlkadım', 'Atakum', 'Canik', 'Tekkeköy', 'Bafra', 'Çarşamba', 'Terme', 'Vezirköprü', 'Havza', 'Kavak', 'Ladik', 'Alaçam', 'Salıpazarı', '19 Mayıs', 'Asarcık', 'Ayvacık', 'Yakakent'],
    'Siirt': ['Merkez', 'Kurtalan', 'Baykan', 'Pervari', 'Şirvan', 'Eruh', 'Tillo'],
    'Sinop': ['Merkez', 'Boyabat', 'Durağan', 'Gerze', 'Ayancık', 'Türkeli', 'Erfelek', 'Dikmen', 'Saraydüzü'],
    'Sivas': ['Merkez', 'Şarkışla', 'Yıldızeli', 'Gemerek', 'Suşehri', 'Zara', 'Kangal', 'Divriği', 'Gürün', 'Hafik', 'Koyulhisar', 'İmranlı', 'Altınyayla', 'Akıncılar', 'Doğanşar', 'Gölova', 'Ulaş'],
    'Şanlıurfa': ['Eyyübiye', 'Haliliye', 'Karaköprü', 'Siverek', 'Viranşehir', 'Suruç', 'Akçakale', 'Birecik', 'Ceylanpınar', 'Bozova', 'Halfeti', 'Hilvan', 'Harran'],
    'Şırnak': ['Merkez', 'Cizre', 'Silopi', 'İdil', 'Uludere', 'Beytüşşebap', 'Güçlükonak'],
    'Tekirdağ': ['Süleymanpaşa', 'Çorlu', 'Çerkezköy', 'Ergene', 'Kapaklı', 'Saray', 'Hayrabolu', 'Muratlı', 'Malkara', 'Şarköy', 'Marmaraereğlisi'],
    'Tokat': ['Merkez', 'Turhal', 'Erbaa', 'Niksar', 'Zile', 'Reşadiye', 'Almus', 'Artova', 'Pazar', 'Başçiftlik', 'Sulusaray', 'Yeşilyurt'],
    'Trabzon': ['Ortahisar', 'Akçaabat', 'Yomra', 'Arsin', 'Araklı', 'Of', 'Sürmene', 'Maçka', 'Vakfıkebir', 'Beşikdüzü', 'Çarşıbaşı', 'Çaykara', 'Dernekpazarı', 'Düzköy', 'Hayrat', 'Köprübaşı', 'Şalpazarı', 'Tonya'],
    'Tunceli': ['Merkez', 'Pertek', 'Çemişgezek', 'Hozat', 'Ovacık', 'Mazgirt', 'Nazımiye', 'Pülümür'],
    'Uşak': ['Merkez', 'Eşme', 'Banaz', 'Sivaslı', 'Ulubey', 'Karahallı'],
    'Van': ['İpekyolu', 'Tuşba', 'Edremit', 'Erciş', 'Çaldıran', 'Özalp', 'Muradiye', 'Başkale', 'Gevaş', 'Gürpınar', 'Saray', 'Bahçesaray', 'Çatak'],
    'Yalova': ['Merkez', 'Çınarcık', 'Çiftlikköy', 'Termal', 'Altınova', 'Armutlu'],
    'Yozgat': ['Merkez', 'Sorgun', 'Yerköy', 'Akdağmadeni', 'Boğazlıyan', 'Sarıkaya', 'Çekerek', 'Şefaatli', 'Çayıralan', 'Kadışehri', 'Aydıncık', 'Çandır', 'Saraykent', 'Yenifakılı'],
    'Zonguldak': ['Merkez', 'Ereğli', 'Çaycuma', 'Devrek', 'Alaplı', 'Gökçebey', 'Kilimli', 'Kozlu']
  };

  const cities = Object.keys(turkeyData).sort();
  const [selectedCity, setSelectedCity] = useState('');
  const districts = selectedCity ? turkeyData[selectedCity]?.sort() || [] : [];

  const [showAuthDialog, setShowAuthDialog] = useState(false);

  // Tahmini fiyat aralıkları
  const priceRanges = {
    kombi: {
      'bakim': { min: 700, max: 1500 },
      'ariza': { min: 800, max: 3500 },
      'montaj': { min: 1500, max: 4000 },
      'petek': { min: 400, max: 1200 }
    },
    klima: {
      'bakim': { min: 500, max: 1200 },
      'ariza': { min: 600, max: 2500 },
      'montaj': { min: 1500, max: 4000 },
      'gaz': { min: 800, max: 2000 },
      'sokum': { min: 800, max: 2000 }
    },
    beyaz_esya: {
      'ariza': { min: 400, max: 2500 },
      'bakim': { min: 300, max: 800 },
      'montaj': { min: 200, max: 600 }
    },
    elektrik: {
      'ariza': { min: 300, max: 1500 },
      'priz': { min: 200, max: 800 },
      'aydinlatma': { min: 300, max: 1200 },
      'tesisat': { min: 800, max: 3500 },
      'sigorta': { min: 400, max: 1500 }
    },
    su_tesisati: {
      'tikaniklik': { min: 350, max: 1200 },
      'kacak': { min: 500, max: 2500 },
      'musluk': { min: 300, max: 1000 },
      'tesisat': { min: 600, max: 3000 },
      'termosifon': { min: 500, max: 2000 }
    },
    genel: {
      'tamir': { min: 400, max: 2000 },
      'bakim': { min: 300, max: 1500 },
      'montaj': { min: 500, max: 2500 },
      'diger': { min: 300, max: 2000 }
    }
  };

  const getEstimatedPrice = () => {
    // AI tahmini varsa onu kullan
    if (aiPriceEstimate?.estimate) {
      // "350-500 TL arası" formatından parse et
      const match = aiPriceEstimate.estimate.match(/(\d+)[^\d]+(\d+)/);
      if (match) {
        return { min: parseInt(match[1]), max: parseInt(match[2]) };
      }
    }
    const categoryPrices = priceRanges[category] || priceRanges.genel;
    const servicePrice = categoryPrices[formData.serviceType];
    if (servicePrice) return servicePrice;
    return { min: 300, max: 2000 };
  };
  
  // AI ile fiyat tahmini al
  const fetchAiPriceEstimate = async () => {
    if (!selectedCity || !formData.district || !formData.serviceType) return;
    
    setAiPriceLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/ai/estimate-price`, {
        service_type: category,
        problem_type: formData.serviceType || formData.subService || 'ariza',
        city: selectedCity,
        district: formData.district,
        brand: formData.brand || null,
        model: null,
        details: formData.additionalInfo || null
      });
      
      if (response.data.success) {
        setAiPriceEstimate(response.data.estimate);
      }
    } catch (error) {
      console.error('AI fiyat tahmini hatası:', error);
    } finally {
      setAiPriceLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      setFormData(prev => ({
        ...prev,
        customerName: userData.full_name || '',
        customerPhone: userData.phone || ''
      }));
    }
  }, []);

  const currentCategory = categoryInfo[category] || categoryInfo.genel;
  const structure = formStructure[category] || formStructure.genel;

  // Dinamik adım belirleme
  const getStepConfig = () => {
    if (currentStep === 1) {
      return structure.step1;
    }
    if (currentStep === 2) {
      const step2Key = `step2_${formData.serviceType}`;
      return structure[step2Key] || structure.step2 || null;
    }
    if (currentStep === 3) {
      const step3Key = `step3_${formData.subService}`;
      return structure[step3Key] || structure.step3 || null;
    }
    return null;
  };

  const handleOptionSelect = (optionId) => {
    if (currentStep === 1) {
      setFormData({ ...formData, serviceType: optionId, subService: '', problemDetail: '' });
    } else if (currentStep === 2) {
      setFormData({ ...formData, subService: optionId });
    } else if (currentStep === 3) {
      setFormData({ ...formData, problemDetail: optionId });
    }
  };

  const getTotalSteps = () => {
    // Adımlar: 1. Hizmet Türü, 2. Alt detay, 3. (varsa) Detay, 4. Adres, 5. Zaman, 6. Özet
    let steps = 5; // Minimum
    const step2Key = `step2_${formData.serviceType}`;
    if (structure[step2Key] || structure.step2) steps = 5;
    const step3Key = `step3_${formData.subService}`;
    if (structure[step3Key] || structure.step3) steps = 6;
    return steps;
  };

  const canProceed = () => {
    const stepConfig = getStepConfig();
    if (stepConfig) {
      if (currentStep === 1) return formData.serviceType !== '';
      if (currentStep === 2) return formData.subService !== '' || !stepConfig;
      if (currentStep === 3) return formData.problemDetail !== '' || !stepConfig;
    }
    if (currentStep === getTotalSteps() - 2) return selectedCity !== '' && formData.district !== '' && formData.address !== '';
    if (currentStep === getTotalSteps() - 1) return formData.urgency !== '';
    return true;
  };

  const handleNext = () => {
    if (canProceed()) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      
      // Özet sayfasına geldiyse AI fiyat tahmini al
      if (nextStep === getTotalSteps()) {
        fetchAiPriceEstimate();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const token = localStorage.getItem('auth_token');
    
    if (!token) {
      localStorage.setItem('pending_service_request', JSON.stringify({ category, formData, selectedCity }));
      setShowAuthDialog(true);
      return;
    }
    
    // Form validasyonu
    if (!selectedCity) {
      alert('Lütfen şehir seçin');
      return;
    }
    if (!formData.district) {
      alert('Lütfen ilçe seçin');
      return;
    }
    if (!formData.address) {
      alert('Lütfen adres girin');
      return;
    }
    
    setSubmitting(true);
    
    // Request payload'ı hazırla
    const requestPayload = {
      category: category || 'genel',
      service_type: formData.serviceType || 'genel',
      sub_service: formData.subService || null,
      problem_detail: formData.problemDetail || null,
      brand: formData.brand || null,
      description: formData.additionalInfo || '',
      city: selectedCity,
      district: formData.district,
      address: formData.address,
      urgency: formData.urgency || 'normal',
      scheduled_date: formData.scheduledDate || null,
      scheduled_time: formData.scheduledTime || null,
      images: formData.images || [],
      estimated_price: aiPriceEstimate?.estimate || null
    };
    
    console.log('[ServiceRequest] Submitting:', requestPayload);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/service-request`,
        requestPayload,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000 // 30 saniye timeout
        }
      );

      console.log('[ServiceRequest] Response:', response.data);

      if (response.data.success) {
        localStorage.removeItem('pending_service_request');
        
        // Track conversion for Google Ads
        trackServiceRequest(category, aiPriceEstimate?.estimated_price || 500);
        
        setCurrentStep(getTotalSteps() + 1); // Başarı ekranı
      } else {
        alert('Talep oluşturulamadı: ' + (response.data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('[ServiceRequest] Error:', error);
      console.error('[ServiceRequest] Error response:', error.response?.data);
      
      // Network hatası (internet bağlantısı, timeout vb.)
      if (!error.response) {
        alert('Bağlantı hatası oluştu. İnternet bağlantınızı kontrol edip tekrar deneyin.');
        return;
      }
      
      // Token hatası kontrolü
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('auth_token');
        alert('Oturumunuz sona ermiş. Lütfen tekrar giriş yapın.');
        setShowAuthDialog(true);
        return;
      }
      
      // Hata mesajını düzgün formatta al
      let errorMessage = 'Lütfen tekrar deneyin.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert('Bir hata oluştu: ' + errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAuthClose = () => {
    setShowAuthDialog(false);
    const token = localStorage.getItem('auth_token');
    if (token) {
      handleSubmit();
    }
  };

  const totalSteps = getTotalSteps();
  const CategoryIcon = currentCategory.icon;

  const renderDynamicStep = () => {
    const stepConfig = getStepConfig();
    if (!stepConfig) return null;

    return (
      <div className="space-y-4">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{stepConfig.title}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {stepConfig.options.map((option) => {
            const Icon = option.icon;
            const isSelected = 
              (currentStep === 1 && formData.serviceType === option.id) ||
              (currentStep === 2 && formData.subService === option.id) ||
              (currentStep === 3 && formData.problemDetail === option.id);
            
            return (
              <button
                key={option.id}
                onClick={() => handleOptionSelect(option.id)}
                className={`p-4 rounded-xl border-2 transition-all text-left flex items-start gap-3 ${
                  isSelected
                    ? 'border-[#006D77] bg-[#E0F2F4]'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                {Icon && (
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-[#006D77]' : 'bg-gray-100'}`}>
                    <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{option.label}</h3>
                  {option.desc && <p className="text-sm text-gray-500 mt-0.5">{option.desc}</p>}
                </div>
                {isSelected && <CheckCircle className="w-5 h-5 text-[#006D77]" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAddressStep = () => (
    <div className="space-y-4">
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Hizmet adresi</h2>
      <p className="text-gray-500 mb-4">Servis yapılacak adresi girin</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-700 font-medium mb-2 block">Şehir *</Label>
          <select
            value={selectedCity}
            onChange={(e) => {
              setSelectedCity(e.target.value);
              setFormData({...formData, city: e.target.value, district: ''});
            }}
            className="w-full rounded-xl border border-gray-200 p-3 text-gray-700 focus:border-[#006D77] focus:ring-1 focus:ring-[#006D77]"
          >
            <option value="">Şehir seçin</option>
            {cities.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-gray-700 font-medium mb-2 block">İlçe *</Label>
          <select
            value={formData.district}
            onChange={(e) => setFormData({...formData, district: e.target.value})}
            className="w-full rounded-xl border border-gray-200 p-3 text-gray-700 focus:border-[#006D77] focus:ring-1 focus:ring-[#006D77]"
            disabled={!selectedCity}
          >
            <option value="">{selectedCity ? 'İlçe seçin' : 'Önce şehir seçin'}</option>
            {districts.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <Label className="text-gray-700 font-medium mb-2 block">Açık Adres *</Label>
        <Textarea
          value={formData.address}
          onChange={(e) => setFormData({...formData, address: e.target.value})}
          className="rounded-xl"
          placeholder="Mahalle, sokak, bina no, daire no..."
          rows={3}
        />
      </div>
    </div>
  );

  const renderTimeStep = () => (
    <div className="space-y-4">
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Ne zaman yapılsın?</h2>
      <p className="text-gray-500 mb-4">Hizmet zamanını seçin</p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => setFormData({...formData, urgency: 'urgent', scheduledDate: new Date().toISOString().split('T')[0]})}
          className={`p-5 rounded-xl border-2 transition-all text-left ${
            formData.urgency === 'urgent' ? 'border-[#E29578] bg-[#FEF3F0]' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${formData.urgency === 'urgent' ? 'bg-[#E29578]' : 'bg-gray-100'}`}>
              <AlertCircle className={`w-5 h-5 ${formData.urgency === 'urgent' ? 'text-white' : 'text-gray-500'}`} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Acil</h3>
              <p className="text-sm text-gray-500">En kısa sürede</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setFormData({...formData, urgency: 'scheduled'})}
          className={`p-5 rounded-xl border-2 transition-all text-left ${
            formData.urgency === 'scheduled' ? 'border-[#006D77] bg-[#E0F2F4]' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${formData.urgency === 'scheduled' ? 'bg-[#006D77]' : 'bg-gray-100'}`}>
              <Calendar className={`w-5 h-5 ${formData.urgency === 'scheduled' ? 'text-white' : 'text-gray-500'}`} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">İleri Tarih</h3>
              <p className="text-sm text-gray-500">Tarih seçin</p>
            </div>
          </div>
        </button>
      </div>

      {formData.urgency === 'scheduled' && (
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <Label className="text-gray-700 font-medium mb-2 block">Tarih</Label>
            <Input
              type="date"
              value={formData.scheduledDate}
              onChange={(e) => setFormData({...formData, scheduledDate: e.target.value})}
              min={new Date().toISOString().split('T')[0]}
              className="rounded-xl"
            />
          </div>
          <div>
            <Label className="text-gray-700 font-medium mb-2 block">Saat</Label>
            <select
              value={formData.scheduledTime}
              onChange={(e) => setFormData({...formData, scheduledTime: e.target.value})}
              className="w-full rounded-xl border border-gray-200 p-3"
            >
              <option value="">Saat seçin</option>
              <option value="09:00-12:00">09:00 - 12:00</option>
              <option value="12:00-15:00">12:00 - 15:00</option>
              <option value="15:00-18:00">15:00 - 18:00</option>
              <option value="18:00-21:00">18:00 - 21:00</option>
            </select>
          </div>
        </div>
      )}

      <div className="mt-4">
        <Label className="text-gray-700 font-medium mb-2 block">Ek Bilgi (Opsiyonel)</Label>
        <Textarea
          value={formData.additionalInfo}
          onChange={(e) => setFormData({...formData, additionalInfo: e.target.value})}
          className="rounded-xl"
          placeholder="Eklemek istediğiniz bilgi varsa yazın..."
          rows={3}
        />
      </div>

      {/* Resim Yükleme Alanı */}
      <div className="mt-6">
        <Label className="text-gray-700 font-medium mb-3 block flex items-center gap-2">
          <Camera className="w-5 h-5 text-[#006D77]" />
          Arıza Fotoğrafı Ekle (Opsiyonel)
        </Label>
        <p className="text-sm text-gray-500 mb-3">Sorunu daha iyi anlamamız için fotoğraf ekleyebilirsiniz (Max 3 adet, 5MB)</p>
        
        <div className="grid grid-cols-3 gap-3">
          {/* Yüklenen Resimler */}
          {formData.images.map((imageUrl, index) => (
            <div key={index} className="relative aspect-square rounded-xl overflow-hidden border-2 border-[#006D77] bg-gray-100">
              <img 
                src={`${API_URL}${imageUrl}`}
                alt={`Yüklenen ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => handleRemoveImage(index)}
                className="absolute top-1 right-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          
          {/* Yükleme Butonu - 3'ten az resim varsa göster */}
          {formData.images.length < 3 && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-[#006D77] bg-gray-50 hover:bg-[#E0F2F4] flex flex-col items-center justify-center gap-2 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadingImage ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#006D77]"></div>
              ) : (
                <>
                  <Camera className="w-8 h-8 text-gray-400" />
                  <span className="text-xs text-gray-500 font-medium">Fotoğraf Ekle</span>
                </>
              )}
            </button>
          )}
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          multiple
          onChange={handleImageUpload}
          className="hidden"
          data-testid="image-upload-input"
        />
      </div>
    </div>
  );

  const renderSummaryStep = () => (
    <div className="space-y-4">
      <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Talep Özeti</h2>
      <p className="text-gray-500 mb-4">Bilgilerinizi kontrol edin</p>
      
      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <div className="flex justify-between py-2 border-b border-gray-200">
          <span className="text-gray-500">Hizmet</span>
          <span className="font-medium text-gray-900">{currentCategory.name}</span>
        </div>
        <div className="flex justify-between py-2 border-b border-gray-200">
          <span className="text-gray-500">Tür</span>
          <span className="font-medium text-gray-900">{formData.serviceType}</span>
        </div>
        {formData.subService && (
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="text-gray-500">Detay</span>
            <span className="font-medium text-gray-900">{formData.subService}</span>
          </div>
        )}
        {formData.brand && (
          <div className="flex justify-between py-2 border-b border-gray-200">
            <span className="text-gray-500">Marka</span>
            <span className="font-medium text-gray-900">{formData.brand}</span>
          </div>
        )}
        <div className="flex justify-between py-2 border-b border-gray-200">
          <span className="text-gray-500">Adres</span>
          <span className="font-medium text-gray-900">{formData.district}, {selectedCity}</span>
        </div>
        <div className="flex justify-between py-2">
          <span className="text-gray-500">Zaman</span>
          <span className="font-medium text-gray-900">
            {formData.urgency === 'urgent' ? 'Acil' : `${formData.scheduledDate} ${formData.scheduledTime}`}
          </span>
        </div>
        
        {/* Yüklenen Resimler */}
        {formData.images.length > 0 && (
          <div className="pt-3 border-t border-gray-200">
            <span className="text-gray-500 text-sm block mb-2">Eklenen Fotoğraflar</span>
            <div className="flex gap-2">
              {formData.images.map((imageUrl, index) => (
                <div key={index} className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
                  <img 
                    src={`${API_URL}${imageUrl}`}
                    alt={`Fotoğraf ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Tahmini Fiyat - Modern Tasarım */}
      <div className="bg-gradient-to-br from-[#006D77] to-[#0A9396] rounded-2xl p-5 text-white relative overflow-hidden">
        {/* Dekoratif elementler */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative z-10">
          {/* Başlık */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">🤖</span>
            <span className="text-white/90 text-sm font-medium">Yapay Zeka Fiyat Tahmini</span>
            {aiPriceLoading && (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin ml-2"></div>
            )}
          </div>
          
          {/* Fiyat */}
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-3xl md:text-4xl font-bold">
              {getEstimatedPrice().min.toLocaleString('tr-TR')} - {getEstimatedPrice().max.toLocaleString('tr-TR')}
            </span>
            <span className="text-xl font-semibold text-white/80">₺</span>
          </div>
          
          {/* AI Açıklaması */}
          {aiPriceEstimate?.ai_explanation && (
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 mt-3">
              <p className="text-white/90 text-sm leading-relaxed">
                {aiPriceEstimate.ai_explanation}
              </p>
            </div>
          )}
          
          {/* Alt bilgi */}
          <div className="flex items-center gap-4 mt-4 text-white/70 text-xs">
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span>{selectedCity}</span>
            </div>
            <div className="flex items-center gap-1">
              <Settings className="w-3 h-3" />
              <span>İşçilik ücreti</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bilgi notu */}
      <div className="flex items-start gap-2 bg-amber-50 rounded-xl p-3 border border-amber-200">
        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-amber-700 text-xs">
          Bu fiyat yapay zeka tarafından hesaplanmış tahmini bir aralıktır. Gerçek fiyat, arıza durumuna ve parça ihtiyacına göre değişebilir.
        </p>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center py-8">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Talebiniz Alındı!</h2>
      <p className="text-gray-500 mb-6">En kısa sürede ustalarımız sizinle iletişime geçecek.</p>
      <Button onClick={() => navigate('/hesabim')} className="bg-[#006D77] hover:bg-[#005a63] text-white rounded-xl px-8">
        Taleplerime Git
      </Button>
    </div>
  );

  const renderCurrentStep = () => {
    const stepConfig = getStepConfig();
    
    // Başarı ekranı
    if (currentStep > totalSteps) {
      return renderSuccessStep();
    }
    
    // Dinamik sorular (step1, step2, step3)
    if (stepConfig) {
      return renderDynamicStep();
    }
    
    // Adres adımı
    if (currentStep === totalSteps - 2) {
      return renderAddressStep();
    }
    
    // Zaman adımı
    if (currentStep === totalSteps - 1) {
      return renderTimeStep();
    }
    
    // Özet adımı
    if (currentStep === totalSteps) {
      return renderSummaryStep();
    }
    
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <section className="pt-24 pb-12">
        <div className="max-w-2xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${currentCategory.color} mb-4`}>
              <CategoryIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">{currentCategory.name}</h1>
          </div>

          {/* Progress */}
          {currentStep <= totalSteps && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-500">Adım {currentStep}/{totalSteps}</span>
                <span className="text-sm font-medium text-[#006D77]">{Math.round((currentStep / totalSteps) * 100)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-[#006D77] to-[#0A9396] transition-all duration-300"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8">
            {renderCurrentStep()}

            {/* Navigation Buttons */}
            {currentStep <= totalSteps && (
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="rounded-xl"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>

                {currentStep < totalSteps ? (
                  <Button
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className="bg-[#006D77] hover:bg-[#005a63] text-white rounded-xl"
                  >
                    Devam
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="bg-[#E29578] hover:bg-[#D67A56] text-white rounded-xl disabled:opacity-50"
                  >
                    {submitting ? 'Gönderiliyor...' : 'Talebi Gönder'}
                    {!submitting && <CheckCircle className="w-4 h-4 ml-2" />}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
      <AuthDialog open={showAuthDialog} onClose={handleAuthClose} skipReload={true} />
    </div>
  );
};

export default ServiceRequestForm;
